import { formatAddress } from '@/helpers/formatAddress';
import { formatPhoneNumber } from '@/helpers/formatPhoneNumber';
import {
  isPackageEditable,
  PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE,
  shouldShowPackageUnderReviewAlert,
} from '@/helpers/packageEditability';
import usePermissions from '@/hooks/usePermissions';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import { useGetDocumentsQuery } from '@/services/documents';
import {
  useSaveInstallGeothermalPackageMutation,
  useSubmitInstallGeothermalPackageMutation,
} from '@/services/installGeothermalPackage';
import {
  Alert,
  Box,
  Button,
  DateInput,
  FormikSelectInputNative,
  FormikTextInput,
  Modal,
  toast,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, Formik, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import {
  Applicant,
  ApplicantType,
  DeepPartial,
  GeothermalEquipmentType,
  GeothermalEquipmentTypeLabels,
  GeothermalIndoorUnitType,
  GeothermalIndoorUnitTypeDefaultEquipmentTypes,
  GeothermalIndoorUnitTypeLabels,
  GeothermalInstallPackageFileCategory,
  GeothermalLoopTypeLabels,
  GeothermalManufacturer,
  GeothermalSystem,
  InstallGeothermalPackage as IInstallGeothermalPackage,
  InstallationPackagePhotoCategory,
  MilestoneType,
  QueueStatus,
  QueueType,
  RequirementFlagSettingsType,
  UserPermissions,
} from 'types';

import { convertToISODateTimeString, DATE_FORMAT } from '../../helpers/dates';
import { useGetAccountQuery } from '../../services/accounts';
import { isErrorWithData } from '../../services/helpers';
import { useGetQueueByAccountIdAndTypeQuery } from '../../services/queues';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { ActionsList } from '../ActionsList';
import { useAuth } from '../auth/authProvider';
import { isInternalUser } from '../auth/RequireIsInternalUser';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { ContractDocumentSection } from '../MilestonePackagePartials/ContractDocumentSection';
import { Footer } from '../MilestonePackagePartials/Footer';
import { AttestationSection } from '../MilestonePackagePartials/geothermal/AttestationSection';
import { InstallPhotos } from '../MilestonePackagePartials/geothermal/InstallPhotos';
import { SystemPhotos } from '../MilestonePackagePartials/geothermal/SystemPhotos';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import { HVACAttestationModal } from '../ProductionValueCheck/HvacAttestationModal';
import { FlagBox } from '../QueueFlags/FlagBox';
import { QueueFlagsContext, QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Box direction="row" borderColor="separator" borderWidth="0 0 xs 0" padding="xs 0">
    <Box width="40" fontWeight="medium">
      {label}
    </Box>
    <Box width="60">{value}</Box>
  </Box>
);

const SystemDesignSummary = ({ system }: { system: GeothermalSystem }) => {
  const loopLabel = system.loopType ? GeothermalLoopTypeLabels[system.loopType] : '—';
  const indoorLabel = system.indoorUnitType ? GeothermalIndoorUnitTypeLabels[system.indoorUnitType] : '—';

  return (
    <Box padding="md" background="tertiary" radius="md" fontSize="sm">
      <SummaryRow label="Loop Type" value={loopLabel} />
      <SummaryRow label="Indoor Unit Type" value={indoorLabel} />
      <SummaryRow label="Capacity" value={system.capacityTons ? `${system.capacityTons} tons` : '—'} />
      <SummaryRow label="DHW Assist" value={system.dhwAssist ? 'Yes' : 'No'} />
      <SummaryRow
        label="Auxiliary Heat"
        value={system.auxiliaryHeat ? `Yes — ${system.auxiliaryHeatType || ''}` : 'No'}
      />
      <SummaryRow
        label="Lease Amount"
        value={system.leaseAmount ? `$${system.leaseAmount.toLocaleString()}` : '—'}
      />
      {system.drillingCost != null && system.drillingCost > 0 && (
        <SummaryRow label="Drilling Cost" value={`$${system.drillingCost.toLocaleString()}`} />
      )}
    </Box>
  );
};

interface InstallPackageProps {
  canSubmitInstallPackage: boolean;
}

const InstallGeothermalPackageSchema = yup.object().shape({
  systemInstallDate: yup.string().required('System Install Date is a required field.'),
  projectAttestationCheckbox: yup.boolean().oneOf([true], 'Attestation is a required field.'),
  systems: yup
    .array()
    .of(
      yup.object().shape({
        manufacturer: yup.string().required('Manufacturer is required.'),
        monitoringId: yup.string().required('Monitoring ID is required.'),
        equipment: yup
          .array()
          .of(
            yup.object().shape({
              model: yup.string().required('Model is required.'),
              serialNumber: yup.string().required('Serial Number is required.'),
            }),
          )
          .min(1, 'At least one equipment entry is required.'),
      }),
    )
    .min(1, 'At least one system is required.'),
});

const manufacturerOptions = [
  { label: 'Select manufacturer...', value: '' },
  ...Object.values(GeothermalManufacturer).map((mfg) => ({ label: mfg, value: mfg })),
];

const projectAttestLabel =
  'I attest that all required permitting has been obtained, the pressure test on the loop has been completed and passed, the loop has been properly flushed and purged, the installation meets all applicable code requirements, the system meets warranty eligibility requirements, and manufacturer registration has been completed.';

export const formatInstallGeothermalPackageData = (values: FormikValues): DeepPartial<IInstallGeothermalPackage> => {
  return {
    systemDesign: {
      isPreliminary: false,
      systems: values.systems || [],
    },
    systemInstallDate: values.systemInstallDate
      ? { palmetto: convertToISODateTimeString(values.systemInstallDate) }
      : undefined,
    projectAttestation: values.projectAttestationCheckbox
      ? { attestmentText: values.projectAttestationCheckbox ? projectAttestLabel : '' }
      : undefined,
  };
};

const documentsPreviouslyUploadedToGeothermalInstallation = [
  InstallationPackagePhotoCategory.equipmentDataPlate,
  InstallationPackagePhotoCategory.mechanicalRoomWideShot,
  InstallationPackagePhotoCategory.flowCenter,
  InstallationPackagePhotoCategory.loopFieldPhoto,
  InstallationPackagePhotoCategory.electricalDisconnect,
  InstallationPackagePhotoCategory.thermostat,
];

export const InstallGeothermalPackage = ({ canSubmitInstallPackage }: InstallPackageProps) => {
  const navigate = useNavigate();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const permissions = usePermissions();
  const isLightReachQCTech = permissions.includes(UserPermissions.lightReachQCTech);
  const { id = '', action } = useParams<{ id: string; action: ReviewPackageActions }>();
  const { data: queue, isLoading: isQueueLoading } = useGetQueueByAccountIdAndTypeQuery(
    { accountId: id, type: QueueType.installPackage },
    { skip: !id },
  );
  const { getFlagsByFlagType } = useContext(QueueFlagsContext);
  const checkboxCategories = useMemo(() => Object.values(GeothermalInstallPackageFileCategory), []);
  const showCheckboxes = isInternalUser(claims, true);

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id, { skip: !id });
  const primaryApplicant = account?.applicants.find(
    (applicant: Applicant) => applicant.type === ApplicantType.primary,
  );
  const systemInstallDate = (values: any): Date | null => {
    return values.systemInstallDate ? DateTime.fromFormat(values.systemInstallDate, DATE_FORMAT).toJSDate() : null;
  };

  const handleDateChange = useCallback((date: Date | [Date, Date] | null, setFieldValue: any) => {
    if (!date || !(date instanceof Date)) return;
    setFieldValue('systemInstallDate', DateTime.fromJSDate(date).toUTC().toFormat(DATE_FORMAT));
  }, []);

  const minDate = DateTime.fromISO('2022-01-01').toJSDate();
  const maxDate = DateTime.local().endOf('day').toJSDate();
  const [triggerSubmit] = useSubmitInstallGeothermalPackageMutation();
  const [triggerSave] = useSaveInstallGeothermalPackageMutation();

  const {
    isOpen: isAttestationOpen,
    handleOpen: setAttestationSubmissionOpen,
    handleClose: setAttestationSubmissionClose,
  } = useOpenClose();
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [userAttestation, setUserAttestation] = useState(false);
  const isLoading = isAccountLoading || isQueueLoading;

  const { data: wasADocumentPreviouslyUploaded } = useGetDocumentsQuery(
    {
      accountId: id,
      documentTypeFilter: documentsPreviouslyUploadedToGeothermalInstallation,
    },
    { skip: !id },
  );

  const { data: installationDocuments } = useGetDocumentsQuery(
    {
      accountId: id,
      documentTypeFilter: [InstallationPackagePhotoCategory.geothermalInstallationPhotos],
    },
    { skip: !id },
  );

  const customerContractFlags = getFlagsByFlagType(RequirementFlagSettingsType.customerContract);
  const geothermalInstallationPhotosFlag = getFlagsByFlagType(
    RequirementFlagSettingsType.geothermalInstallationPhotos,
  );
  const showInstallPhotosForInitialSubmission =
    !wasADocumentPreviouslyUploaded?.length &&
    !isFinCoUser &&
    (!queue || (queue && queue.status === QueueStatus.submitted));
  const showInstallPhotosForEPCUser =
    !isFinCoUser && (geothermalInstallationPhotosFlag?.length || (queue && queue.status === QueueStatus.submitted));

  const triggerSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.install);
  const {
    isOpen: isQuantityModalOpen,
    handleOpen: openQuantityModal,
    handleClose: closeQuantityModal,
  } = useOpenClose();
  const [pendingSplit, setPendingSplit] = useState<{ index: number; quantity: number } | null>(null);
  const VALIDATION_ERROR = 'ValidationError';

  useEffect(() => {
    if (!action || !ReviewPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
    if (action === ReviewPackageActions.review && (!isFinCoUser || !isLightReachQCTech)) {
      if (queue) {
        navigate(`/accounts/${id}/installation-package/edit`, { replace: true });
      } else {
        navigate(`/accounts/${id}/installation-package/create`, { replace: true });
      }
    }
  }, [id, navigate, action, isFinCoUser, isLightReachQCTech, queue]);

  const accountInitialValues = useMemo(() => {
    if (!account) return {};
    const sd = account?.systemDesign;
    const rawSystems = sd?.systems || [];

    const systems = rawSystems.length
      ? rawSystems.map((sys: any, i: number) => {
          const indoorUnitType = sys.indoorUnitType || sd?.indoorUnitType;
          const manufacturer = sys.manufacturer || sys.equipment?.[0]?.manufacturer || '';
          const persisted: any[] = Array.isArray(sys.equipment) ? sys.equipment : [];
          const requiredTypes = GeothermalIndoorUnitTypeDefaultEquipmentTypes[
            indoorUnitType as GeothermalIndoorUnitType
          ] ?? [GeothermalEquipmentType.packagedHeatPump];

          const claimed = new Set<number>();
          const equipment = requiredTypes.map((type, idx) => {
            // Match on type; fall back to position for items persisted before `type` existed,
            // so an existing packaged system's model/serial aren't orphaned.
            let source = persisted.findIndex((item, j) => !claimed.has(j) && item?.type === type);
            if (source === -1 && persisted[idx] && !persisted[idx].type && !claimed.has(idx)) {
              source = idx;
            }
            if (source !== -1) claimed.add(source);
            const item = source === -1 ? {} : persisted[source];

            return {
              type,
              manufacturer,
              model: item.model || '',
              serialNumber: item.serialNumber || '',
              description: item.description || '',
              quantity: item.quantity ?? 1,
            };
          });

          return {
            name: sys.name || sd?.name || `System ${i + 1}`,
            loopType: sys.loopType || sd?.loopType,
            indoorUnitType,
            capacityTons: sys.capacityTons ?? sd?.capacityTons,
            dhwAssist: sys.dhwAssist ?? sd?.dhwAssist ?? false,
            auxiliaryHeat: sys.auxiliaryHeat ?? sd?.auxiliaryHeat ?? false,
            auxiliaryHeatType: sys.auxiliaryHeatType || sd?.auxiliaryHeatType,
            leaseAmount: sys.leaseAmount ?? sd?.totalLeaseAmount,
            drillingCost: sys.drillingCost ?? sd?.drillingCost ?? sd?.totalDrillingCost,
            manufacturer,
            equipment,
            monitoringId: sys.monitoringId || '',
          };
        })
      : [
          {
            name: 'System 1',
            manufacturer: '',
            monitoringId: '',
            equipment: [
              {
                type: GeothermalEquipmentType.packagedHeatPump,
                manufacturer: '',
                model: '',
                serialNumber: '',
                description: '',
                quantity: 1,
              },
            ],
          },
        ];

    return {
      systemInstallDate: account?.systemInstallDate?.palmetto
        ? DateTime.fromISO(account.systemInstallDate.palmetto).toUTC().toFormat(DATE_FORMAT)
        : '',
      systems,
    };
  }, [account]);

  const canSubmitReview = action === ReviewPackageActions.review && queue;
  const initialValues = { ...accountInitialValues };

  const validateInstallationDocuments = (): void => {
    if (!installationDocuments?.length && !queue) {
      const validationError = new Error('At least one installation document is required.');
      validationError.name = VALIDATION_ERROR;
      throw validationError;
    }
  };

  const handleSubmit = async (values: FormikValues, { setSubmitting }: any) => {
    const formValues = formatInstallGeothermalPackageData(values);
    if (!userAttestation) {
      setAttestationSubmissionOpen();
      return;
    }
    try {
      validateInstallationDocuments();
    } catch (e) {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      return;
    }
    try {
      setSubmitting(true);
      await triggerSubmit({ accountId: id, installPackage: formValues as IInstallGeothermalPackage }).unwrap();
      toast.success('Install Package Submitted');
      navigate(`/accounts/${id}`);
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error submitting install package');
    } finally {
      setSubmitting(false);
      setUserAttestation(false);
      setAttestationSubmissionClose();
    }
  };

  const handleSave = async ({
    accountId,
    data,
    targetStatus,
  }: {
    accountId: string;
    data: FormikValues;
    targetStatus?: QueueStatus;
  }) => {
    try {
      if (targetStatus) {
        validateInstallationDocuments();
      }
      const formValues = formatInstallGeothermalPackageData(data);
      await triggerSave({ accountId, savePackage: formValues }).unwrap();
      triggerSuccessMessage();
    } catch (e) {
      console.error(e);
      if (isErrorWithData(e)) {
        throw new Error(e.data.message);
      } else if (e instanceof Error && e.name === VALIDATION_ERROR) {
        throw e;
      } else {
        throw new Error('Error saving Install Package');
      }
    }
  };

  const canEditPackage = isPackageEditable({ queue, isFinCoUser });
  const showUnderReviewAlert = shouldShowPackageUnderReviewAlert({ queue, isFinCoUser });

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }

  const attestationModalText =
    'By submitting this package, I attest that all equipment listed is accurate to the equipment installed for the customer, and that all equipment is working to manufacturer specifications.';

  return (
    <>
      <QueueFlagsProvider queueId={queue?.id}>
        <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.installPackage} />
        <MainContainer>
          <Formik
            initialValues={initialValues}
            validationSchema={InstallGeothermalPackageSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ setFieldValue, values, setSubmitting, isSubmitting, touched, errors }) => (
              <>
                <EnhancedFormikForm>
                  <ActionsList
                    milestoneType={MilestoneType.install}
                    accountId={queue?.account?.id}
                    canSubmitPackage={canSubmitInstallPackage}
                    isLoading={isLoading}
                    isFirstSubmission={!queue}
                    canSubmitReview={canSubmitReview}
                    onSave={handleSave}
                    disabled={!canEditPackage}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    queue={queue}
                  />

                  {queue && (queue.status === QueueStatus.rejected || isFinCoUser) ? (
                    <QueueBanner
                      setIsNotesOpen={setIsNotesOpen}
                      queueType={QueueType.installPackage}
                      isFinCoUser={isFinCoUser}
                    />
                  ) : null}
                  {showUnderReviewAlert ? (
                    <Alert
                      variant="warning"
                      title="Install Package In Review Queue"
                      message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                      hasIcon
                    />
                  ) : null}
                  {action === ReviewPackageActions.review && !canSubmitReview ? (
                    <Alert
                      variant="warning"
                      title="Install Package Not Submitted"
                      message="This install package is not ready to be reviewed yet. It has not been submitted."
                      hasIcon
                    />
                  ) : null}
                  {(isFinCoUser || (customerContractFlags?.length || 0) > 0) && (
                    <ContractDocumentSection showSeparatorBorder={true} />
                  )}
                  <FormSection title="System Install Date">
                    <FlagBox
                      dataKey="System Install Date"
                      baseCategory="systemInstallDate"
                      flagType={RequirementFlagSettingsType.systemInstallDateGeothermal}
                      displayWithNoQueue
                    >
                      <Field
                        label=""
                        name="systemInstallDate"
                        id="systemInstallDate"
                        dateFormat={DATE_FORMAT}
                        width="2xl"
                        value={values.systemInstallDate}
                        component={DateInput}
                        datePickerProps={{
                          maxDate,
                          minDate,
                          onChange: (date: Date | [Date, Date] | null) => handleDateChange(date, setFieldValue),
                          openToDate: systemInstallDate(values) ?? undefined,
                        }}
                        textInputProps={{
                          onClear: () => setFieldValue('systemInstallDate', ''),
                        }}
                        isDisabled={isSubmitting || !canEditPackage}
                        error={touched.systemInstallDate && (errors.systemInstallDate as string)}
                      />
                    </FlagBox>
                  </FormSection>
                  <FormSection title="Equipment &amp; Materials">
                    <FieldArray name="systems">
                      {({ remove }) => (
                        <Box gap="lg">
                          {values.systems?.map((system: any, index: number) => (
                            <Box key={`system-${index}`} padding="lg" background="secondary" radius="md" gap="lg">
                              <Box direction="row" justifyContent="space-between" alignItems="center">
                                <Box fontSize="lg" fontWeight="medium">
                                  {system.name || `System ${index + 1}`}
                                </Box>
                                {values.systems.length > 1 && (
                                  <Button
                                    variant="tertiary"
                                    tone="neutral"
                                    size="sm"
                                    onClick={() => remove(index)}
                                    iconPrefix="trash"
                                    isDisabled={isSubmitting || !canEditPackage}
                                  />
                                )}
                              </Box>
                              <SystemDesignSummary system={system} />
                              <Box fontSize="md" fontWeight="medium">
                                Installed Equipment
                              </Box>
                              <Box direction={{ base: 'column', tablet: 'row' }} gap="lg">
                                <Box flex="auto" minWidth="0">
                                  <Field
                                    label="Manufacturer"
                                    name={`systems.${index}.manufacturer`}
                                    id={`systems.${index}.manufacturer`}
                                    component={FormikSelectInputNative}
                                    options={manufacturerOptions}
                                    isDisabled={true}
                                    helpText="Changing manufacturer requires a new customer contract. If needed, void current contract and re-quote."
                                  />
                                </Box>
                              </Box>
                              {system.equipment?.map((item: any, equipIndex: number) => (
                                <Box
                                  as="fieldset"
                                  key={item.type ?? equipIndex}
                                  gap="md"
                                  borderWidth="0"
                                  padding="0"
                                >
                                  {system.equipment.length > 1 && (
                                    <Box as="legend" fontSize="sm" fontWeight="medium" color="body-secondary">
                                      {GeothermalEquipmentTypeLabels[item.type as GeothermalEquipmentType]}
                                    </Box>
                                  )}
                                  <Box direction={{ base: 'column', tablet: 'row' }} gap="lg">
                                    <Box flex="auto" minWidth="0">
                                      <Field
                                        label="Model"
                                        name={`systems.${index}.equipment.${equipIndex}.model`}
                                        id={`systems.${index}.equipment.${equipIndex}.model`}
                                        component={FormikTextInput}
                                        isDisabled={isSubmitting || !canEditPackage}
                                      />
                                    </Box>
                                    <Box flex="auto" minWidth="0">
                                      <Field
                                        label="Serial Number"
                                        name={`systems.${index}.equipment.${equipIndex}.serialNumber`}
                                        id={`systems.${index}.equipment.${equipIndex}.serialNumber`}
                                        component={FormikTextInput}
                                        isDisabled={isSubmitting || !canEditPackage}
                                      />
                                    </Box>
                                  </Box>
                                  <Box direction={{ base: 'column', tablet: 'row' }} gap="lg">
                                    <Box flex="auto" minWidth="0">
                                      <Field
                                        label="Description (optional)"
                                        name={`systems.${index}.equipment.${equipIndex}.description`}
                                        id={`systems.${index}.equipment.${equipIndex}.description`}
                                        component={FormikTextInput}
                                        isDisabled={isSubmitting || !canEditPackage}
                                      />
                                    </Box>
                                    {system.equipment.length === 1 && (
                                      <Box width={{ base: '2xl', tablet: 'lg' }}>
                                        <Field
                                          label="Quantity"
                                          type="number"
                                          name={`systems.${index}.equipment.0.quantity`}
                                          id={`systems.${index}.equipment.0.quantity`}
                                          component={FormikTextInput}
                                          min={1}
                                          max={10}
                                          isDisabled={isSubmitting || !canEditPackage}
                                          onChange={(event: any) => {
                                            const val = parseInt(event?.target?.value, 10);
                                            if (!isNaN(val) && val > 1 && canEditPackage) {
                                              setPendingSplit({ index, quantity: val });
                                              openQuantityModal();
                                            }
                                          }}
                                          onBlur={(event: any) => {
                                            if (event?.target?.value?.trim() === '') {
                                              setFieldValue(`systems.${index}.equipment.0.quantity`, 1);
                                            }
                                          }}
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                              <FlagBox
                                dataKey={`System Monitoring ID ${index + 1}`}
                                baseCategory="monitoringId"
                                flagType={RequirementFlagSettingsType.systemMonitoringId}
                                displayWithNoQueue
                              >
                                <Box direction={{ base: 'column', tablet: 'row' }} gap="lg">
                                  <Box flex="auto" minWidth="0">
                                    <Field
                                      label="Monitoring ID"
                                      name={`systems.${index}.monitoringId`}
                                      id={`systems.${index}.monitoringId`}
                                      component={FormikTextInput}
                                      isDisabled={isSubmitting || !canEditPackage}
                                    />
                                  </Box>
                                </Box>
                              </FlagBox>
                            </Box>
                          ))}
                          <Modal
                            isOpen={isQuantityModalOpen}
                            onDismiss={() => {
                              setPendingSplit(null);
                              closeQuantityModal();
                            }}
                            maxWidth="4xl"
                            ariaLabelledBy="quantityModalHeader"
                          >
                            <Modal.Header
                              id="quantityModalHeader"
                              title="Additional Heat Pump Detected"
                              onDismiss={() => {
                                setPendingSplit(null);
                                closeQuantityModal();
                              }}
                            />
                            <Modal.Body>
                              Each heat pump system must be entered separately so that equipment details
                              (manufacturer, model, serial number) can be captured per unit. Would you like to add{' '}
                              {pendingSplit && pendingSplit.quantity - 1 > 1
                                ? `${pendingSplit.quantity - 1} new systems`
                                : 'a new system'}
                              ?
                            </Modal.Body>
                            <Modal.Footer>
                              <Button
                                variant="secondary"
                                tone="neutral"
                                onClick={() => {
                                  setPendingSplit(null);
                                  closeQuantityModal();
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="primary"
                                onClick={() => {
                                  if (pendingSplit) {
                                    const { index: splitIndex, quantity: qty } = pendingSplit;
                                    const sys = values.systems[splitIndex];
                                    const newSystems = [...values.systems];
                                    newSystems[splitIndex] = {
                                      ...sys,
                                      equipment: (sys.equipment || []).map((item: any, i: number) =>
                                        i === 0 ? { ...item, quantity: 1 } : item,
                                      ),
                                    };
                                    for (let i = 1; i < qty; i++) {
                                      newSystems.push({
                                        ...sys,
                                        name: `System ${newSystems.length + 1}`,
                                        leaseAmount: 0,
                                        drillingCost: 0,
                                        manufacturer: '',
                                        monitoringId: '',
                                        equipment: (sys.equipment || []).map((item: any) => ({
                                          ...item,
                                          model: '',
                                          serialNumber: '',
                                          description: '',
                                          quantity: 1,
                                        })),
                                      });
                                    }
                                    setFieldValue('systems', newSystems);
                                  }
                                  setPendingSplit(null);
                                  closeQuantityModal();
                                }}
                              >
                                Add System
                              </Button>
                            </Modal.Footer>
                          </Modal>
                        </Box>
                      )}
                    </FieldArray>
                  </FormSection>
                  {isFinCoUser && (
                    <FormSection title="Customer Information">
                      <table>
                        <tbody>
                          <tr>
                            <td>Partner Licensed Org:</td>
                            <td>
                              <Link to={`/settings/organizations/${account?.licensedOrganizationId}`}>
                                {account?.licensedOrganizationId?.toUpperCase() ?? ''}
                              </Link>
                            </td>
                          </tr>
                          <tr>
                            <td>Partner Org:</td>
                            <td>
                              <Link to={`/settings/organizations/${account?.organizationId}`}>
                                {account?.organizationId?.toUpperCase() ?? ''}
                              </Link>
                            </td>
                          </tr>
                          <tr>
                            <td>Primary Contact Details:</td>
                            <td>
                              {primaryApplicant?.firstName} {primaryApplicant?.lastName}
                              {' - '}
                              {primaryApplicant?.email}
                              {' - '}
                              {primaryApplicant?.phoneNumber ? formatPhoneNumber(primaryApplicant.phoneNumber) : ''}
                            </td>
                          </tr>
                          <tr>
                            <td>Property Address:</td>
                            <td>{formatAddress(account?.address)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </FormSection>
                  )}
                  {showInstallPhotosForInitialSubmission ? (
                    <InstallPhotos showCheckboxes={showCheckboxes} checkboxCategories={checkboxCategories} />
                  ) : (
                    <>
                      {(isFinCoUser || showInstallPhotosForEPCUser) && (
                        <InstallPhotos showCheckboxes={showCheckboxes} checkboxCategories={checkboxCategories} />
                      )}
                      <SystemPhotos showCheckboxes={showCheckboxes} checkboxCategories={checkboxCategories} />
                    </>
                  )}
                  {action !== ReviewPackageActions.review && (
                    <AttestationSection
                      projectAttestLabel={projectAttestLabel}
                      setFieldValue={setFieldValue}
                      values={values}
                      isSubmitting={isSubmitting}
                      touched={touched}
                      errors={errors}
                      isPackageEditable={canEditPackage}
                    />
                  )}
                  <Footer
                    canSubmitPackage={canSubmitInstallPackage}
                    isLoading={isLoading}
                    isFirstSubmission={!queue}
                    canSubmitReview={canSubmitReview}
                    onSave={handleSave}
                    disabled={!canEditPackage}
                  />
                </EnhancedFormikForm>
                <HVACAttestationModal
                  title="Install Submission Confirmation"
                  label={attestationModalText}
                  values={values}
                  handleSubmit={handleSubmit}
                  isOpen={isAttestationOpen}
                  onDismiss={setAttestationSubmissionClose}
                  onAttestationConfirm={setUserAttestation}
                  isLoading={isSubmitting}
                  setSubmitting={setSubmitting}
                />
              </>
            )}
          </Formik>
        </MainContainer>
      </QueueFlagsProvider>
    </>
  );
};
